import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import QuoteCommentsPanel from '../components/QuoteCommentsPanel';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Plus, CheckCircle2, Clock, XCircle, Upload, Check, Truck, IndianRupee, Package, Image, User, Calendar, Eye, Trash2, Save, Edit3, FileSpreadsheet, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MaterialRequestsPage() {
  const navigate = useNavigate();
  const { hasAnyRole } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approvalData, setApprovalData] = useState({
    approved_amount: '',
    bus_details: '',
    bus_fare: '',
    approval_remarks: '',
    action: 'approve',
    photo: null
  });
  const [approving, setApproving] = useState(false);
  
  // Supervisor accept state
  const [acceptingId, setAcceptingId] = useState(null);
  
  // Edit mode state for DM slide panel
  const [editingRequest, setEditingRequest] = useState(null);
  const [editData, setEditData] = useState({
    approved_amount: '',
    bus_fare: '',
    bus_details: '',
    approval_remarks: '',
    photo: null,
    deletePhoto: false
  });
  const [saving, setSaving] = useState(false);
  
  // Project Head approval state
  const [phApprovalDialogOpen, setPhApprovalDialogOpen] = useState(false);
  const [phSelectedRequest, setPhSelectedRequest] = useState(null);
  const [phApprovalAmount, setPhApprovalAmount] = useState('');
  const [phApproving, setPhApproving] = useState(false);

  // Export states
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [exporting, setExporting] = useState(false);
  const [phBudgetInfo, setPhBudgetInfo] = useState(null);
  const [loadingBudget, setLoadingBudget] = useState(false);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('all');

  // Define action filter options based on user role
  const getActionFilterOptions = () => {
    const options = [{ value: 'all', label: 'All Actions' }];
    
    if (hasAnyRole(['Delivery Manager', 'Super Admin'])) {
      options.push({ value: 'dm_approve', label: 'DM Approval Pending' });
    }
    
    if (hasAnyRole(['Project Head', 'Super Admin'])) {
      options.push({ value: 'ph_approve', label: 'PH Approval Pending' });
    }
    
    if (hasAnyRole(['Site Supervisor', 'Super Admin'])) {
      options.push({ value: 'sv_accept', label: 'SV Accept Pending' });
    }
    
    options.push({ value: 'no_action', label: 'No Action Required' });
    
    return options;
  };

  // PH Rejection state
  const [phRejectDialogOpen, setPhRejectDialogOpen] = useState(false);
  const [phRejectRemarks, setPhRejectRemarks] = useState('');
  const [phRejecting, setPhRejecting] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/material-requests`);
      
      // Fetch presigned URLs for photos
      const requestsWithUrls = await Promise.all(
        response.data.map(async (request) => {
          if (request.photo_path) {
            try {
              const urlRes = await axios.get(`${API}/files/url/${encodeURIComponent(request.photo_path)}`);
              return { ...request, photo_url: urlRes.data.url };
            } catch (e) {
              console.error('Error getting photo URL:', e);
              return request;
            }
          }
          return request;
        })
      );
      
      setRequests(requestsWithUrls);
    } catch (error) {
      toast.error('Failed to load material requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    setApproving(true);
    try {
      const formData = new FormData();
      // Calculate total amount: approved_amount + bus_fare
      const baseAmount = parseFloat(approvalData.approved_amount) || 0;
      const busFare = parseFloat(approvalData.bus_fare) || 0;
      const totalAmount = baseAmount + busFare;
      
      formData.append('approved_amount', totalAmount.toString());
      formData.append('bus_details', approvalData.bus_details);
      formData.append('bus_fare', approvalData.bus_fare || '0');
      formData.append('approval_remarks', approvalData.approval_remarks);
      formData.append('action', approvalData.action);
      if (approvalData.photo) {
        formData.append('photo', approvalData.photo);
      }

      await axios.post(`${API}/material-requests/${requestId}/approve`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Material request ${approvalData.action}d successfully`);
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setApproving(false);
    }
  };

  // Project Head approval handler
  const handleProjectHeadApprove = async () => {
    if (!phApprovalAmount || parseFloat(phApprovalAmount) <= 0) {
      toast.error('Please enter a valid approval amount');
      return;
    }
    
    setPhApproving(true);
    try {
      await axios.post(`${API}/material-requests/${phSelectedRequest.id}/ph-approve`, {
        approved_amount: parseFloat(phApprovalAmount)
      });
      toast.success('Material request approved by Project Head');
      setPhApprovalDialogOpen(false);
      setPhSelectedRequest(null);
      setPhApprovalAmount('');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setPhApproving(false);
    }
  };

  const openPhApprovalDialog = async (request) => {
    setPhSelectedRequest(request);
    setPhApprovalAmount('');
    setPhBudgetInfo(null);
    setPhApprovalDialogOpen(true);
    
    // Fetch budget info
    setLoadingBudget(true);
    try {
      const response = await axios.get(`${API}/quotes/${request.quote_id}/material-budget`);
      setPhBudgetInfo(response.data);
    } catch (error) {
      console.error('Failed to load budget info', error);
    } finally {
      setLoadingBudget(false);
    }
  };

  // Project Head rejection handler
  const openPhRejectDialog = (request) => {
    setPhSelectedRequest(request);
    setPhRejectRemarks('');
    setPhRejectDialogOpen(true);
  };

  const handleProjectHeadReject = async () => {
    if (!phRejectRemarks.trim()) {
      toast.error('Remarks are mandatory');
      return;
    }
    if (phRejectRemarks.length > 500) {
      toast.error('Remarks must be 500 characters or less');
      return;
    }

    setPhRejecting(true);
    try {
      await axios.post(`${API}/material-requests/${phSelectedRequest.id}/ph-reject`, {
        remarks: phRejectRemarks
      });
      toast.success('Material request rejected');
      setPhRejectDialogOpen(false);
      setPhSelectedRequest(null);
      setPhRejectRemarks('');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setPhRejecting(false);
    }
  };

  // Supervisor accept handler
  const handleSupervisorAccept = async (requestId) => {
    setAcceptingId(requestId);
    try {
      await axios.post(`${API}/material-requests/${requestId}/supervisor-accept`);
      toast.success('Material request accepted successfully');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept request');
    } finally {
      setAcceptingId(null);
    }
  };

  // Export to Excel handler
  const handleExportToExcel = async () => {
    if (!exportFromDate || !exportToDate) {
      toast.error('Please select both From and To dates');
      return;
    }
    
    if (new Date(exportFromDate) > new Date(exportToDate)) {
      toast.error('From date cannot be after To date');
      return;
    }
    
    setExporting(true);
    try {
      const response = await axios.get(
        `${API}/material-requests/export?from_date=${exportFromDate}&to_date=${exportToDate}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Material_Requests_${exportFromDate}_to_${exportToDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel file downloaded successfully');
      setExportDialogOpen(false);
      setExportFromDate('');
      setExportToDate('');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  // Initialize edit mode for DM
  const initEditMode = (request) => {
    setEditingRequest(request.id);
    setEditData({
      approved_amount: request.approved_amount || '',
      bus_fare: request.bus_fare || '',
      bus_details: request.bus_details || '',
      approval_remarks: request.approval_remarks || '',
      photo: null,
      deletePhoto: false
    });
  };

  // Save edited details (DM only)
  const handleSaveDetails = async (requestId) => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('approved_amount', editData.approved_amount);
      formData.append('bus_fare', editData.bus_fare || '0');
      formData.append('bus_details', editData.bus_details);
      formData.append('approval_remarks', editData.approval_remarks);
      formData.append('delete_photo', editData.deletePhoto);
      if (editData.photo) {
        formData.append('photo', editData.photo);
      }

      await axios.put(`${API}/material-requests/${requestId}/update-details`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Details updated successfully');
      setEditingRequest(null);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update details');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status, request = null) => {
    const variants = {
      pending: { className: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
      accept: { className: 'bg-blue-100 text-blue-800 border-blue-200', icon: CheckCircle2 },
      approved: { className: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle2 },
      rejected: { className: 'bg-red-100 text-red-800 border-red-200', icon: XCircle }
    };
    const config = variants[status] || variants.pending;
    const Icon = config.icon;
    
    const badge = (
      <Badge className={`${config.className} cursor-pointer`}>
        <Icon className="h-3 w-3 mr-1" />
        {status.toUpperCase()}
      </Badge>
    );
    
    // Show slide panel only for accept/approved/rejected requests with details
    if ((status === 'accept' || status === 'approved' || status === 'rejected') && request && (request.approved_amount || request.approval_remarks)) {
      // Check if DM can edit (status is 'accept' and user is DM or Super Admin)
      const canEdit = status === 'accept' && hasAnyRole(['Delivery Manager', 'Super Admin']);
      const isEditing = editingRequest === request.id;
      
      return (
        <Sheet onOpenChange={(open) => {
          if (!open) {
            setEditingRequest(null);
          }
        }}>
          <SheetTrigger asChild>
            <div className="inline-flex items-center gap-1 cursor-pointer hover:opacity-80">
              {badge}
              <Eye className="h-3 w-3 text-gray-400" />
            </div>
          </SheetTrigger>
          <SheetContent side="right" className="w-[400px] sm:w-[450px] overflow-y-auto">
            <SheetHeader className="pb-4 border-b">
              <SheetTitle className="flex items-center gap-2">
                {status === 'approved' || status === 'accept' ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
                Details
                {canEdit && !isEditing && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="ml-auto"
                    onClick={() => initEditMode(request)}
                  >
                    <Edit3 className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                )}
              </SheetTitle>
              <p className="text-sm text-muted-foreground">Quote: {request.quote_no}</p>
            </SheetHeader>
            
            <div className="space-y-6 py-6">
              {/* Editable Mode for DM */}
              {isEditing ? (
                <>
                  {/* Editable Approved Amount */}
                  <div className="space-y-2">
                    <Label htmlFor="edit_amount">Approved Amount (₹) *</Label>
                    <Input
                      id="edit_amount"
                      type="number"
                      step="0.01"
                      value={editData.approved_amount}
                      onChange={(e) => setEditData({ ...editData, approved_amount: e.target.value })}
                      placeholder="Enter approved amount"
                    />
                  </div>
                  
                  {/* Editable Bus Fare */}
                  <div className="space-y-2">
                    <Label htmlFor="edit_bus_fare">Bus/Transport Fare (₹)</Label>
                    <Input
                      id="edit_bus_fare"
                      type="number"
                      step="0.01"
                      min="0"
                      value={editData.bus_fare}
                      onChange={(e) => setEditData({ ...editData, bus_fare: e.target.value })}
                      placeholder="Enter transport fare"
                    />
                    {editData.bus_fare && parseFloat(editData.bus_fare) > 0 && (
                      <p className="text-xs text-blue-600">
                        Total: ₹{((parseFloat(editData.approved_amount) || 0) + (parseFloat(editData.bus_fare) || 0)).toLocaleString('en-IN')}
                      </p>
                    )}
                  </div>
                  
                  {/* Editable Bus Details */}
                  <div className="space-y-2">
                    <Label htmlFor="edit_bus_details">Bus/Transport Details *</Label>
                    <Input
                      id="edit_bus_details"
                      value={editData.bus_details}
                      onChange={(e) => setEditData({ ...editData, bus_details: e.target.value })}
                      placeholder="e.g., Bus No, Driver Name"
                    />
                  </div>
                  
                  {/* Editable Materials to deliver */}
                  <div className="space-y-2">
                    <Label htmlFor="edit_remarks">Materials to deliver *</Label>
                    <Textarea
                      id="edit_remarks"
                      value={editData.approval_remarks}
                      onChange={(e) => setEditData({ ...editData, approval_remarks: e.target.value })}
                      placeholder="Enter materials to be delivered"
                      rows={3}
                    />
                  </div>
                  
                  {/* Photo Management */}
                  <div className="space-y-2">
                    <Label>Material Photo</Label>
                    {request.photo_url && !editData.deletePhoto && !editData.photo && (
                      <div className="space-y-2">
                        <div className="rounded-lg border overflow-hidden bg-gray-100">
                          <img 
                            src={request.photo_url}
                            alt="Material"
                            className="w-full h-auto max-h-[150px] object-contain"
                          />
                        </div>
                        <Button 
                          type="button" 
                          variant="destructive" 
                          size="sm"
                          onClick={() => setEditData({ ...editData, deletePhoto: true })}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete Photo
                        </Button>
                      </div>
                    )}
                    {(editData.deletePhoto || !request.photo_url) && !editData.photo && (
                      <p className="text-sm text-gray-500">No photo</p>
                    )}
                    <div className="space-y-1">
                      <Label htmlFor="edit_photo">Upload New Photo (Max 5MB)</Label>
                      <Input
                        id="edit_photo"
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file && file.size > 5 * 1024 * 1024) {
                            toast.error('Photo must be less than 5MB');
                            e.target.value = '';
                            return;
                          }
                          setEditData({ ...editData, photo: file, deletePhoto: false });
                        }}
                      />
                      {editData.photo && (
                        <p className="text-xs text-green-600">New photo selected: {editData.photo.name}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Save / Cancel Buttons */}
                  <div className="flex gap-2 pt-4 border-t">
                    <Button
                      onClick={() => handleSaveDetails(request.id)}
                      disabled={saving || !editData.approved_amount || !editData.bus_details || !editData.approval_remarks}
                      className="flex-1"
                    >
                      <Save className="h-4 w-4 mr-1" />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setEditingRequest(null)}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {/* Read-only View */}
                  {/* Approved Amount - Hidden for Site Supervisors */}
                  {request.approved_amount && hasAnyRole(['Delivery Manager', 'Super Admin', 'Project Head']) && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                        <IndianRupee className="h-4 w-4" />
                        Approved Amount
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-2xl font-bold text-green-700">
                          ₹{request.approved_amount.toLocaleString('en-IN')}
                        </p>
                        {request.bus_fare > 0 && (
                          <p className="text-sm text-green-600 mt-1">
                            (Includes ₹{request.bus_fare.toLocaleString('en-IN')} transport fare)
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Bus/Transport Details */}
                  {request.bus_details && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                        <Truck className="h-4 w-4" />
                        Bus/Transport Details
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <p className="text-sm">{request.bus_details}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Materials to deliver */}
                  {request.approval_remarks && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                        <Package className="h-4 w-4" />
                        Materials to deliver
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <p className="text-sm whitespace-pre-wrap">{request.approval_remarks}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Photo */}
                  {request.photo_url && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                        <Image className="h-4 w-4" />
                        Material Photo
                      </div>
                      <div className="rounded-lg border overflow-hidden bg-gray-100">
                        <img 
                          src={request.photo_url}
                          alt="Material"
                          className="w-full h-auto max-h-[300px] object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="hidden items-center justify-center h-32 text-gray-500 text-sm">
                          Unable to load photo
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Processed Date */}
                  {request.approved_at && (
                    <div className="space-y-2 pt-4 border-t">
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                        <Calendar className="h-4 w-4" />
                        Processed on
                      </div>
                      <p className="text-sm text-gray-700">
                        {new Date(request.approved_at).toLocaleString('en-IN', {
                          dateStyle: 'long',
                          timeStyle: 'short'
                        })}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </SheetContent>
        </Sheet>
      );
    }
    
    return badge;
  };

  // Filter requests based on search query and action filter
  const filteredRequests = requests.filter(request => {
    // Search filter
    const matchesSearch = !searchQuery || request.quote_no?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Action filter
    let matchesAction = true;
    if (actionFilter !== 'all') {
      const isDMPending = request.status === 'pending';
      const isPHPending = request.status === 'dm_approved';
      const isSVAcceptPending = request.status === 'approved' && !request.accepted_by_supervisor;
      
      switch (actionFilter) {
        case 'dm_approve':
          matchesAction = isDMPending;
          break;
        case 'ph_approve':
          matchesAction = isPHPending;
          break;
        case 'sv_accept':
          matchesAction = isSVAcceptPending;
          break;
        case 'no_action':
          matchesAction = !isDMPending && !isPHPending && !isSVAcceptPending;
          break;
        default:
          matchesAction = true;
      }
    }
    
    return matchesSearch && matchesAction;
  });

  if (loading) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-lg">Loading material requests...</div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="space-y-6" data-testid="material-requests-page">
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Material Requests
            </h1>
            <p className="text-muted-foreground mt-2">
              {filteredRequests.length} request(s) found
              {searchQuery && ` (filtered from ${requests.length})`}
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setExportDialogOpen(true)}
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Export to Excel
            </Button>
            {hasAnyRole(['Site Supervisor', 'Super Admin', 'Project Head']) && (
              <Button onClick={() => navigate('/material-requests/create')} data-testid="create-material-request-button">
                <Plus className="mr-2 h-4 w-4" />
                New Material Request
              </Button>
            )}
          </div>
        </div>

        {/* Search and Filter Box */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by Quote Number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-quote-input"
            />
          </div>
          
          {/* Action Filter - Only show if user has action column */}
          {hasAnyRole(['Division Manager', 'Project Head', 'Site Supervisor', 'Super Admin', 'Delivery Manager']) && (
            <div className="w-full sm:w-64">
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger data-testid="action-filter">
                  <SelectValue placeholder="Filter by Action" />
                </SelectTrigger>
                <SelectContent>
                  {getActionFilterOptions().map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <Card className="overflow-hidden glass-table">
          <div className="overflow-x-auto">
            <table className="w-full table-fixed" data-testid="material-requests-table">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-4 py-4 text-left text-sm font-medium">Quote No</th>
                  <th className="px-4 py-4 text-left text-sm font-medium">Requested By</th>
                  <th className="px-4 py-4 text-left text-sm font-medium">Remarks</th>
                  {hasAnyRole(['Delivery Manager', 'Super Admin', 'Project Head']) && (
                    <th className="px-4 py-4 text-right text-sm font-medium">Approved Amount</th>
                  )}
                  <th className="px-4 py-4 text-left text-sm font-medium">Status</th>
                  <th className="px-4 py-4 text-left text-sm font-medium">Date</th>
                  {hasAnyRole(['Site Supervisor', 'Super Admin']) && (
                    <th className="px-4 py-4 text-center text-sm font-medium">Accept</th>
                  )}
                  {hasAnyRole(['Project Head', 'Super Admin']) && (
                    <th className="px-4 py-4 text-center text-sm font-medium">PH Approve</th>
                  )}
                  {hasAnyRole(['Delivery Manager', 'Super Admin']) && (
                    <th className="px-4 py-4 text-center text-sm font-medium">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-6 py-12 text-center text-muted-foreground">
                      {searchQuery ? `No material requests found for "${searchQuery}"` : 'No material requests found'}
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map((request) => (
                    <tr key={request.id} className="hover:bg-muted/50 transition-colors">
                      <td className="px-4 py-4">
                        <span className="font-mono text-sm font-medium whitespace-nowrap">{request.quote_no}</span>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <span className="break-words">{request.requested_by_name}</span>
                      </td>
                      <td className="px-4 py-4 text-sm max-w-[300px]">
                        <p className="break-words">
                          {request.remarks || '-'}
                        </p>
                      </td>
                      {hasAnyRole(['Delivery Manager', 'Super Admin', 'Project Head']) && (
                        <td className="px-4 py-4 text-sm text-right font-mono whitespace-nowrap">
                          <span className="inline-flex items-center">
                            {request.approved_amount ? `₹${request.approved_amount.toLocaleString('en-IN')}` : '-'}
                            {request.approved_amount && <QuoteCommentsPanel quoteId={request.quote_id} quoteNo={request.quote_no} />}
                          </span>
                        </td>
                      )}
                      <td className="px-4 py-4">{getStatusBadge(request.status, request)}</td>
                      <td className="px-4 py-4 text-sm whitespace-nowrap">
                        {new Date(request.created_at).toLocaleDateString()}
                      </td>
                      {hasAnyRole(['Site Supervisor', 'Super Admin']) && (
                        <td className="px-4 py-4 text-center">
                          {request.status === 'accept' && (
                            <Button
                              size="sm"
                              onClick={() => handleSupervisorAccept(request.id)}
                              disabled={acceptingId === request.id}
                              className="bg-blue-600 hover:bg-blue-700"
                              data-testid={`accept-button-${request.id}`}
                            >
                              <Check className="h-3 w-3 mr-1" />
                              {acceptingId === request.id ? 'Accepting...' : 'Accept'}
                            </Button>
                          )}
                          {request.status === 'approved' && (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Accepted
                            </Badge>
                          )}
                          {(request.status === 'pending' || request.status === 'rejected') && (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </td>
                      )}
                      {hasAnyRole(['Project Head', 'Super Admin']) && (
                        <td className="px-4 py-4 text-center">
                          {request.status === 'pending' && !request.ph_approved && !request.ph_rejected && (
                            <div className="flex justify-center gap-2">
                              <Button
                                size="sm"
                                onClick={() => openPhApprovalDialog(request)}
                                className="bg-green-600 hover:bg-green-700"
                                data-testid={`ph-approve-button-${request.id}`}
                              >
                                <Check className="h-3 w-3 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => openPhRejectDialog(request)}
                                data-testid={`ph-reject-button-${request.id}`}
                              >
                                <XCircle className="h-3 w-3 mr-1" />
                                Reject
                              </Button>
                            </div>
                          )}
                          {request.ph_approved && (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Approved
                            </Badge>
                          )}
                          {request.ph_rejected && (
                            <Badge className="bg-red-100 text-red-800">
                              <XCircle className="h-3 w-3 mr-1" />
                              Rejected
                            </Badge>
                          )}
                          {request.status !== 'pending' && !request.ph_approved && !request.ph_rejected && (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </td>
                      )}
                      {hasAnyRole(['Delivery Manager', 'Super Admin']) && (
                        <td className="px-4 py-4 text-center w-24">
                          {request.status === 'pending' && (
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedRequest(request);
                                    setApprovalData({
                                      approved_amount: request.ph_approved_amount || '',
                                      bus_details: '',
                                      bus_fare: '',
                                      approval_remarks: '',
                                      action: 'approve',
                                      photo: null
                                    });
                                  }}
                                  data-testid={`process-button-${request.id}`}
                                >
                                  Process
                                </Button>
                              </DialogTrigger>
                              <DialogContent data-testid="approval-dialog">
                                <DialogHeader>
                                  <DialogTitle>Process Material Request</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                  <div className="space-y-2">
                                    <Label>Quote: {request.quote_no}</Label>
                                    <p className="text-sm text-muted-foreground break-all whitespace-pre-wrap max-h-20 overflow-y-auto">
                                      Remarks: {request.remarks || 'N/A'}
                                    </p>
                                  </div>
                                  <div className="space-y-2">
                                    <Label htmlFor="action">Action</Label>
                                    <div className="flex gap-2">
                                      <Button
                                        type="button"
                                        variant={approvalData.action === 'approve' ? 'default' : 'outline'}
                                        onClick={() => setApprovalData({ ...approvalData, action: 'approve' })}
                                        className="flex-1"
                                      >
                                        Approve
                                      </Button>
                                      <Button
                                        type="button"
                                        variant={approvalData.action === 'reject' ? 'destructive' : 'outline'}
                                        onClick={() => setApprovalData({ ...approvalData, action: 'reject' })}
                                        className="flex-1"
                                      >
                                        Reject
                                      </Button>
                                    </div>
                                  </div>
                                  {approvalData.action === 'approve' && (
                                    <div className="space-y-2">
                                      <Label htmlFor="approved_amount">Approved Amount (₹) *</Label>
                                      <Input
                                        id="approved_amount"
                                        type="number"
                                        step="0.01"
                                        value={approvalData.approved_amount}
                                        onChange={(e) => setApprovalData({ ...approvalData, approved_amount: e.target.value })}
                                        placeholder="Enter approved amount"
                                        required
                                        data-testid="approved-amount-input"
                                      />
                                    </div>
                                  )}
                                  {approvalData.action === 'approve' && (
                                    <div className="space-y-2">
                                      <Label htmlFor="bus_details">Bus/Transport Details *</Label>
                                      <Input
                                        id="bus_details"
                                        value={approvalData.bus_details}
                                        onChange={(e) => setApprovalData({ ...approvalData, bus_details: e.target.value })}
                                        placeholder="e.g., Bus No, Driver Name"
                                        required
                                        data-testid="bus-details-input"
                                      />
                                    </div>
                                  )}
                                  {approvalData.action === 'approve' && (
                                    <div className="space-y-2">
                                      <Label htmlFor="bus_fare">Bus/Transport Fare (₹)</Label>
                                      <Input
                                        id="bus_fare"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={approvalData.bus_fare}
                                        onChange={(e) => setApprovalData({ ...approvalData, bus_fare: e.target.value })}
                                        placeholder="Enter transport fare (will be added to approved amount)"
                                        data-testid="bus-fare-input"
                                      />
                                      {approvalData.bus_fare && parseFloat(approvalData.bus_fare) > 0 && (
                                        <p className="text-xs text-blue-600">
                                          Total Amount: ₹{((parseFloat(approvalData.approved_amount) || 0) + (parseFloat(approvalData.bus_fare) || 0)).toLocaleString('en-IN')}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                  <div className="space-y-2">
                                    <Label htmlFor="approval_remarks">Materials to deliver *</Label>
                                    <Textarea
                                      id="approval_remarks"
                                      value={approvalData.approval_remarks}
                                      onChange={(e) => setApprovalData({ ...approvalData, approval_remarks: e.target.value })}
                                      placeholder="Enter materials to be delivered"
                                      rows={3}
                                      required
                                      data-testid="approval-remarks-input"
                                    />
                                  </div>
                                  {approvalData.action === 'approve' && (
                                    <div className="space-y-2">
                                      <Label htmlFor="photo">Upload Photo (Mandatory Max 5MB) *</Label>
                                      <Input
                                        id="photo"
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                          const file = e.target.files[0];
                                          if (file && file.size > 5 * 1024 * 1024) {
                                            toast.error('Photo must be less than 5MB');
                                            e.target.value = '';
                                            return;
                                          }
                                          setApprovalData({ ...approvalData, photo: file });
                                        }}
                                        data-testid="photo-input"
                                      />
                                      {!approvalData.photo && (
                                        <p className="text-xs text-red-500">Photo is required</p>
                                      )}
                                    </div>
                                  )}
                                  <Button
                                    onClick={() => handleApprove(request.id)}
                                    disabled={
                                      approving || 
                                      !approvalData.approval_remarks ||
                                      (approvalData.action === 'approve' && (
                                        !approvalData.photo || 
                                        !approvalData.bus_details || 
                                        !approvalData.approved_amount
                                      ))
                                    }
                                    className="w-full"
                                    data-testid="confirm-button"
                                  >
                                    {approving ? 'Processing...' : `Confirm ${approvalData.action}`}
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                          )}
                          {request.status !== 'pending' && (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Project Head Approval Dialog */}
      <Dialog open={phApprovalDialogOpen} onOpenChange={setPhApprovalDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Approve Material Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-gray-50 rounded-lg space-y-2 max-w-full overflow-hidden">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Quote No:</span>
                <span className="text-sm font-mono font-medium">{phSelectedRequest?.quote_no}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Date:</span>
                <span className="text-sm font-medium">
                  {phSelectedRequest && new Date(phSelectedRequest.created_at).toLocaleDateString()}
                </span>
              </div>
              <div className="pt-2 border-t">
                <span className="text-sm text-gray-600">Remarks:</span>
                <p className="text-sm mt-1 break-all whitespace-pre-wrap max-h-24 overflow-y-auto">{phSelectedRequest?.remarks || 'N/A'}</p>
              </div>
            </div>
            
            {/* Budget Breakdown */}
            {loadingBudget ? (
              <div className="p-4 bg-blue-50 rounded-lg text-center text-sm text-blue-600">
                Loading budget info...
              </div>
            ) : phBudgetInfo && (
              <div className="p-4 bg-blue-50 rounded-lg space-y-2 border border-blue-200">
                <h4 className="text-sm font-semibold text-blue-800 mb-2">Budget Breakdown</h4>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Budget (Quote Amount):</span>
                  <span className="font-medium">₹{phBudgetInfo.total_budget?.toLocaleString('en-IN')}</span>
                </div>
                {phBudgetInfo.fund_approved > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Fund Requests Approved:</span>
                    <span className="font-medium text-red-600">- ₹{phBudgetInfo.fund_approved?.toLocaleString('en-IN')}</span>
                  </div>
                )}
                {phBudgetInfo.dm_approved > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Material Requests Approved (DM):</span>
                    <span className="font-medium text-red-600">- ₹{phBudgetInfo.dm_approved?.toLocaleString('en-IN')}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Already Approved:</span>
                  <span className="font-medium text-red-600">- ₹{phBudgetInfo.total_already_approved?.toLocaleString('en-IN')}</span>
                </div>
                {(phBudgetInfo.fund_pending > 0 || phBudgetInfo.pending_dm_approval > 0) && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Pending Approval:</span>
                    <span className="font-medium text-orange-600">- ₹{phBudgetInfo.total_pending?.toLocaleString('en-IN')}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm pt-2 border-t border-blue-200">
                  <span className="font-semibold text-blue-800">Available Budget:</span>
                  <span className="font-bold text-green-600">₹{phBudgetInfo.available_budget?.toLocaleString('en-IN')}</span>
                </div>
              </div>
            )}
            
            {/* Margin Percentage - Calculated field (updates in real-time) */}
            {phBudgetInfo && (
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-purple-700">Margin Percentage</span>
                  <span className="text-lg font-bold text-purple-800">
                    {phBudgetInfo?.total_budget && phBudgetInfo.total_budget > 0
                      ? (((parseFloat(phBudgetInfo.total_budget) - (parseFloat(phApprovalAmount || 0) + parseFloat(phBudgetInfo?.total_approved || 0) + parseFloat(phBudgetInfo?.total_pending || 0))) / parseFloat(phBudgetInfo.total_budget)) * 100).toFixed(2)
                      : '0.00'}%
                  </span>
                </div>
                <p className="text-xs text-purple-600 mt-1">
                  Formula: [Quotation - (Approve Amount + Already Approved + Pending)] / Quotation × 100
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="ph_approval_amount">Approve approx. Material Amount (₹) *</Label>
              <Input
                id="ph_approval_amount"
                type="number"
                step="0.01"
                min="0"
                max={phBudgetInfo?.available_budget || undefined}
                value={phApprovalAmount}
                onChange={(e) => setPhApprovalAmount(e.target.value)}
                placeholder="Enter approved amount"
                data-testid="ph-approval-amount-input"
              />
              {phBudgetInfo && phApprovalAmount && parseFloat(phApprovalAmount) > phBudgetInfo.available_budget && (
                <p className="text-xs text-red-600">
                  Amount exceeds available budget (₹{phBudgetInfo.available_budget?.toLocaleString('en-IN')})
                </p>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={handleProjectHeadApprove}
                disabled={phApproving || !phApprovalAmount || (phBudgetInfo && parseFloat(phApprovalAmount) > phBudgetInfo.available_budget)}
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="ph-confirm-approve-button"
              >
                {phApproving ? 'Approving...' : 'Confirm Approval'}
              </Button>
              <Button variant="outline" onClick={() => setPhApprovalDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export to Excel Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Export Material Requests to Excel
            </DialogTitle>
            <DialogDescription>
              Select the date range for Quotation Date to export material requests.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="export_from_date">From Date *</Label>
              <Input
                id="export_from_date"
                type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="export_to_date">To Date *</Label>
              <Input
                id="export_to_date"
                type="date"
                value={exportToDate}
                onChange={(e) => setExportToDate(e.target.value)}
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button
                onClick={handleExportToExcel}
                disabled={exporting || !exportFromDate || !exportToDate}
                className="flex-1"
              >
                {exporting ? 'Exporting...' : 'Ok'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setExportDialogOpen(false);
                  setExportFromDate('');
                  setExportToDate('');
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Project Head Reject Dialog */}
      <Dialog open={phRejectDialogOpen} onOpenChange={setPhRejectDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="ph-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject Material Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Quote: <span className="font-medium">{phSelectedRequest?.quote_no}</span></p>
              <p className="text-sm text-gray-600">Requested By: <span className="font-medium">{phSelectedRequest?.requested_by_name}</span></p>
              <p className="text-sm text-gray-600">Remarks: <span className="font-medium">{phSelectedRequest?.remarks || 'N/A'}</span></p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ph_reject_remarks">
                Remarks * <span className="text-xs text-muted-foreground">({phRejectRemarks.length}/500)</span>
              </Label>
              <Textarea
                id="ph_reject_remarks"
                value={phRejectRemarks}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setPhRejectRemarks(e.target.value);
                  }
                }}
                maxLength={500}
                rows={4}
                placeholder="Enter rejection reason (mandatory)"
                required
                data-testid="ph-reject-remarks-input"
              />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleProjectHeadReject}
                disabled={phRejecting || !phRejectRemarks.trim()}
                variant="destructive"
                className="flex-1"
                data-testid="confirm-ph-reject-button"
              >
                {phRejecting ? 'Processing...' : 'Confirm Rejection'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setPhRejectDialogOpen(false)}
                disabled={phRejecting}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Sidebar>
  );
}
